import { historicalPeriod } from '../shared/regional_prices.js';
import { Controller } from '@hotwired/stimulus';

// Consume the chart's state aggregates; never issue a second request or relabel
// a national snapshot as local data. SSR rows remain intact for indexing.
export default class extends Controller {
  static targets = ['national', 'nationalNote', 'regional', 'status', 'row'];

  connect() {
    this.marketScope = { state: '', slug: 'all', stateLabel: 'United States', trimLabel: 'All trims' };
    this.cache = new Map();
    this.onScope = event => this.applyScope(event.detail);
    this.onRegional = event => this.receiveData(event.detail);
    window.addEventListener('seo:scope-changed', this.onScope);
    window.addEventListener('seo:regional-data', this.onRegional);
    window.dispatchEvent(new CustomEvent('seo:request-scope'));
  }

  disconnect() {
    window.removeEventListener('seo:scope-changed', this.onScope);
    window.removeEventListener('seo:regional-data', this.onRegional);
  }

  applyScope(detail) {
    this.marketScope = { ...this.marketScope, ...detail };
    this.render();
  }

  receiveData({ state, data }) {
    this.cache.set(state, data);
    if (state === this.marketScope.state) this.render();
  }

  highlight(rows) {
    rows.forEach(row => row.classList.toggle('price-trim-row--selected', this.marketScope.slug !== 'all' && row.dataset.trim === this.marketScope.slug));
  }

  render() {
    const { state, stateLabel, slug, trimLabel } = this.marketScope;
    this.nationalTarget.hidden = Boolean(state);
    this.nationalNoteTargets.forEach(note => { note.hidden = Boolean(state); });
    this.regionalTarget.hidden = true;
    this.statusTarget.hidden = !state;
    if (!state) {
      this.highlight(this.rowTargets);
      return;
    }
    const region = stateLabel || state;
    const data = this.cache.get(state);
    if (!data) {
      this.statusTarget.textContent = `${region} · Loading actual state trim prices…`;
      return;
    }
    if (data.status === 'unavailable') {
      this.statusTarget.textContent = `${region} · State trim prices are temporarily unavailable. Choose another state or United States.`;
      return;
    }
    const trims = Array.isArray(data.trims) ? data.trims : [];
    if (!trims.length) {
      this.statusTarget.textContent = `${region} · ${data.basis === 'historical' ? 'No qualifying active listings. Last 3 months: ' : ''}Not enough qualifying listings to show reliable prices by trim. Choose another state or United States.`;
      return;
    }
    const matched = slug === 'all' || trims.some(trim => trim.slug === slug);
    this.statusTarget.textContent = `${region} · ${data.basis === 'historical' ? historicalPeriod(data) : `Current inventory${data.observed_at ? ` · Checked ${data.observed_at}` : ''}.`} Counts and prices are for each trim in this state.${slug !== 'all' ? matched ? ` ${trimLabel || slug} is highlighted.` : ` Not enough qualifying listings for ${trimLabel || slug}; other available trims are shown.` : ''}`;
    const table = document.createElement('table');
    table.className = 'seo-table seo-table--responsive';
    const caption = table.createCaption();
    caption.textContent = `${region} asking prices by trim`;
    const headers = ['Trim', 'Based on', 'Median price', 'Typical range'];
    const head = table.createTHead().insertRow();
    headers.forEach(label => {
      const th = document.createElement('th');
      th.scope = 'col';
      th.textContent = label;
      head.append(th);
    });
    const body = table.createTBody();
    const number = value => value != null && Number.isFinite(Number(value)) ? Number(value).toLocaleString('en-US') : '—';
    const price = value => value != null && Number.isFinite(Number(value)) ? `$${number(value)}` : '—';
    trims.forEach(trim => {
      const row = body.insertRow();
      row.dataset.trim = trim.slug;
      [trim.label, `${number(trim.count)} listings`, price(trim.median), trim.p25 != null && trim.p75 != null ? `${price(trim.p25)}–${price(trim.p75)}` : '—'].forEach((value, index) => {
        const cell = row.insertCell();
        cell.dataset.label = headers[index];
        cell.textContent = value;
      });
    });
    this.highlight([...body.rows]);
    this.regionalTarget.replaceChildren(table);
    this.regionalTarget.hidden = false;
  }
}
